import { ActionEntityType, ImportColumnType, ImportSchemaColumn } from '@pmp/api';
import {
  AdditionalFields,
  ImportTableDataRow,
  TableRowBase,
  TableColumnFormat,
  TableColumns,
  TableEditableCells,
  transformToTableColumns,
} from '@ohpen/enso-ui';

const SELECTED_ROWS_LABELS: Partial<Record<ActionEntityType, string>> = {
  [ActionEntityType.PRODUCTS]: 'pmp.products.labels.xProductsSelected',
  [ActionEntityType.BASE_RATES]: 'pmp.baseRates.labels.xBaseRatesSelected',
  [ActionEntityType.ERC_PROFILES]: 'pmp.erc.labels.xErcProfilesSelected',
  [ActionEntityType.FEE_PROFILES]: 'pmp.fees.labels.xFeeProfilesSelected',
  [ActionEntityType.FEES]: 'pmp.fees.labels.xFeesSelected',
  [ActionEntityType.EXCHANGE_RATES]: 'pmp.exchangeRates.labels.xExchangeRatesSelected',
  [ActionEntityType.EXCLUSIVITY_SETS]: 'pmp.exclusiveProducts.labels.xExclusivitySetsSelected',
  [ActionEntityType.LENDER_FIRMS]: 'lender.firms.labels.xFirmsSelected',
};

export const getSelectedRowsLabel = (importEntity: ActionEntityType): string =>
  SELECTED_ROWS_LABELS[importEntity] ?? 'global.labels.xItemsSelected';

export const getEditableCells = <RowData>(columnKeys: string[], tableData: ImportTableDataRow<RowData>[]): TableEditableCells =>
  tableData?.reduce((obj, row) => {
    const editableColumns = columnKeys.reduce(
      (acc, key) => {
        if (typeof key === 'string') {
          acc[key] = true;
        }
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    return { ...obj, [row.rowNumber]: editableColumns };
  }, {}) ?? {};

export const getAllTableColumns = <RowData>(
  schema: ImportSchemaColumn[],
  additionalFields: AdditionalFields,
): TableColumns<TableRowBase<RowData>> => {
  const typesMapping: Record<ImportColumnType, TableColumnFormat['type']> = {
    [ImportColumnType.ENUMERATION]: 'valueList',
    [ImportColumnType.MULTI_VALUE_ENUMERATION]: 'multiValueList',
    [ImportColumnType.MULTI_VALUE_STRING]: 'multiTag',
    [ImportColumnType.BOOLEAN]: 'boolean',
    [ImportColumnType.DATE]: 'date',
    [ImportColumnType.NUMBER]: 'number',
    [ImportColumnType.STRING]: 'text',
  };
  const columns = schema.map(column => ({
    key: column.key,
    dataIndex: column.key,
    title: column.label,
    options: {
      format: { type: typesMapping[column.type] },
      listName: column.listName ?? undefined,
    },
  }));

  if (!additionalFields || !Object.keys(additionalFields).length) return columns;

  const additionalFieldsColumns = transformToTableColumns(additionalFields) as TableColumns<TableRowBase<RowData>>;

  return [...columns, ...additionalFieldsColumns];
};
